/**
 * Navigation Tools for FlowRide
 * Utility functions for navigation system integration
 */

import { ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { RouteSegment } from "../types/Navigation";
import { DecodePath } from "./ItineraryDecoder";

/**
 * Convert a route (array of coordinates) to route segments
 * @param coordinates - Array of [longitude, latitude] coordinates
 * @returns Array of route segments
 */
export function convertRouteToSegments( coordinates: [number, number][] ): RouteSegment[] {
    if ( coordinates.length < 2 ) {
        return [];
    }

    const segments: RouteSegment[] = [];

    for ( let i = 0; i < coordinates.length - 1; i++ ) {
        const start = coordinates[i];
        const end = coordinates[i + 1];

        if ( start === undefined || end === undefined ) {
            continue;
        }

        const bearing = calculateBearing( start[1], start[0], end[1], end[0] );
        const length = calculateDistance( start[1], start[0], end[1], end[0] );

        segments.push( {
            start,
            end,
            bearing,
            length,
        } );
    }

    return segments;
}

/**
 * Convert GeoJSON LineString to route segments
 * @param lineString - GeoJSON LineString geometry
 * @returns Array of route segments
 */
export function convertGeoJSONToSegments( lineString: {
    "type":        "LineString";
    "coordinates": number[][];
} ): RouteSegment[] {
    if ( lineString.type !== "LineString" ) {
        console.error( "[NavigationTools] Invalid GeoJSON type, expected LineString" );

        return [];
    }

    const coordinates = lineString.coordinates as [number, number][];

    return convertRouteToSegments( coordinates );
}

/**
 * Convert polyline string to route segments
 * @param polyline - Encoded polyline string
 * @param precision - Polyline precision (default: 5)
 * @returns Array of route segments
 */
export function convertPolylineToSegments( polyline: string, precision: number = 5 ): RouteSegment[] {
    const coordinates = decodePolyline( polyline, precision );

    return convertRouteToSegments( coordinates );
}

/**
 * Find nearest segment to a point
 * @param point - Point coordinates [longitude, latitude]
 * @param segments - Array of route segments
 * @returns Index of nearest segment or -1 if no segments
 */
export function findNearestSegment( point: [number, number], segments: RouteSegment[] ): number {
    if ( segments.length === 0 ) {
        return -1;
    }

    let minDistance = Infinity;
    let nearestIndex = 0;

    segments.forEach( ( segment, index ) => {
        const distance = distanceToSegment( point, segment );
        if ( distance < minDistance ) {
            minDistance = distance;
            nearestIndex = index;
        }
    } );

    return nearestIndex;
}

/**
 * Get bearing of nearest road segment in the direction "ahead" of the user.
 * If the user is closer to segment.start, returns start→end bearing; if closer to segment.end, returns end→start (bearing + 180°).
 * @param point - Point coordinates [longitude, latitude]
 * @param segments - Array of route segments
 * @returns Bearing in degrees [0, 360) or null if no segments
 */
export function getNearestRoadBearing(
    point: [number, number],
    segments: RouteSegment[],
): number | null {
    const nearestIndex = findNearestSegment( point, segments );

    if ( nearestIndex === -1 ) {
        return null;
    }

    const segment = segments[nearestIndex];

    if ( segment === undefined ) {
        return null;
    }

    const [ lon, lat ] = point;
    const distToStart = calculateDistance( lat, lon, segment.start[1], segment.start[0] );
    const distToEnd = calculateDistance( lat, lon, segment.end[1], segment.end[0] );

    const result = distToEnd >= distToStart ? segment.bearing : ( segment.bearing + 180 ) % 360;

    return result;
}

/**
 * Calculate distance from point to segment
 * @param point - Point coordinates [longitude, latitude]
 * @param segment - Route segment
 * @returns Distance in meters
 */
export function distanceToSegment( point: [number, number], segment: RouteSegment ): number {
    const [ lon, lat ] = point;
    const [ lon1, lat1 ] = segment.start;
    const [ lon2, lat2 ] = segment.end;

    // Calculate projection of point onto line segment
    const dx = lon2 - lon1;
    const dy = lat2 - lat1;

    const t = Math.max(
        0,
        Math.min( 1, ( ( lon - lon1 ) * dx + ( lat - lat1 ) * dy ) / ( dx * dx + dy * dy ) ),
    );

    const projectedLon = lon1 + t * dx;
    const projectedLat = lat1 + t * dy;

    return calculateDistance( lat, lon, projectedLat, projectedLon );
}

/**
 * Calculate bearing between two points
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Bearing in degrees [0, 360)
 */
export function calculateBearing( lat1: number, lon1: number, lat2: number, lon2: number ): number {
    const φ1 = ( lat1 * Math.PI ) / 180;
    const φ2 = ( lat2 * Math.PI ) / 180;
    const Δλ = ( ( lon2 - lon1 ) * Math.PI ) / 180;

    const y = Math.sin( Δλ ) * Math.cos( φ2 );
    const x = Math.cos( φ1 ) * Math.sin( φ2 ) - Math.sin( φ1 ) * Math.cos( φ2 ) * Math.cos( Δλ );

    const θ = Math.atan2( y, x );
    const bearing = ( ( θ * 180 ) / Math.PI + 360 ) % 360;

    return bearing;
}

/**
 * Calculate distance between two points using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in meters
 */
export function calculateDistance( lat1: number, lon1: number, lat2: number, lon2: number ): number {
    const R = 6371000; // Earth's radius in meters
    const φ1 = ( lat1 * Math.PI ) / 180;
    const φ2 = ( lat2 * Math.PI ) / 180;
    const Δφ = ( ( lat2 - lat1 ) * Math.PI ) / 180;
    const Δλ = ( ( lon2 - lon1 ) * Math.PI ) / 180;

    const a =
        Math.sin( Δφ / 2 ) * Math.sin( Δφ / 2 ) +
        Math.cos( φ1 ) * Math.cos( φ2 ) * Math.sin( Δλ / 2 ) * Math.sin( Δλ / 2 );

    const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

    return R * c;
}

/**
 * Decode polyline string to coordinates
 * @param encoded - Encoded polyline string
 * @param precision - Polyline precision (default: 5)
 * @returns Array of [longitude, latitude] coordinates
 */
export function decodePolyline( encoded: string, precision: number = 5 ): [number, number][] {
    const coordinates: [number, number][] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;
    const factor = Math.pow( 10, precision );

    while ( index < encoded.length ) {
        let b: number;
        let shift = 0;
        let result = 0;

        do {
            b = encoded.charCodeAt( index++ ) - 63;
            result |= ( b & 0x1f ) << shift;
            shift += 5;
        } while ( b >= 0x20 );

        const dlat = ( result & 1 ) !== 0 ? ~( result >> 1 ) : result >> 1;
        lat += dlat;

        shift = 0;
        result = 0;

        do {
            b = encoded.charCodeAt( index++ ) - 63;
            result |= ( b & 0x1f ) << shift;
            shift += 5;
        } while ( b >= 0x20 );

        const dlng = ( result & 1 ) !== 0 ? ~( result >> 1 ) : result >> 1;
        lng += dlng;

        coordinates.push( [ lng / factor, lat / factor ] );
    }

    return coordinates;
}

/**
 * Simplify route segments (reduce number of points)
 * Uses Ramer-Douglas-Peucker algorithm
 * @param segments - Array of route segments
 * @param tolerance - Simplification tolerance in meters (default: 10)
 * @returns Simplified array of route segments
 */
export function simplifySegments( segments: RouteSegment[], tolerance: number = 10 ): RouteSegment[] {
    if ( segments.length <= 2 ) {
        return segments;
    }

    // Convert segments to points
    const points: [number, number][] = [ segments[0].start ];
    segments.forEach( ( segment ) => {
        points.push( segment.end );
    } );

    // Simplify points
    const simplified = simplifyPoints( points, tolerance );

    // Convert back to segments
    return convertRouteToSegments( simplified );
}

/**
 * Simplify array of points using Ramer-Douglas-Peucker algorithm
 * @param points - Array of [longitude, latitude] points
 * @param tolerance - Tolerance in meters
 * @returns Simplified array of points
 */
function simplifyPoints( points: [number, number][], tolerance: number ): [number, number][] {
    if ( points.length <= 2 ) {
        return points;
    }

    const first = points[0];
    const last = points[points.length - 1];

    // Find point with maximum distance from line
    let maxDistance = 0;
    let maxIndex = 0;

    for ( let i = 1; i < points.length - 1; i++ ) {
        const point = points[i];
        const distance = perpendicularDistance( point, first, last );

        if ( distance > maxDistance ) {
            maxDistance = distance;
            maxIndex = i;
        }
    }

    // If max distance is greater than tolerance, recursively simplify
    if ( maxDistance > tolerance ) {
        const left = simplifyPoints( points.slice( 0, maxIndex + 1 ), tolerance );
        const right = simplifyPoints( points.slice( maxIndex ), tolerance );

        // Concatenate results (remove duplicate middle point)
        return [ ...left.slice( 0, -1 ), ...right ];
    } else {
        // Return endpoints only
        return [ first, last ];
    }
}

/**
 * Calculate perpendicular distance from point to line
 * @param point - Point coordinates
 * @param lineStart - Line start coordinates
 * @param lineEnd - Line end coordinates
 * @returns Distance in meters
 */
function perpendicularDistance(
    point: [number, number],
    lineStart: [number, number],
    lineEnd: [number, number],
): number {
    const [ x, y ] = point;
    const [ x1, y1 ] = lineStart;
    const [ x2, y2 ] = lineEnd;

    const dx = x2 - x1;
    const dy = y2 - y1;

    if ( dx === 0 && dy === 0 ) {
        // Line start and end are the same point
        return calculateDistance( y, x, y1, x1 );
    }

    const t = Math.max( 0, Math.min( 1, ( ( x - x1 ) * dx + ( y - y1 ) * dy ) / ( dx * dx + dy * dy ) ) );

    const projectedX = x1 + t * dx;
    const projectedY = y1 + t * dy;

    return calculateDistance( y, x, projectedY, projectedX );
}

/**
 * Calculate total route length
 * @param segments - Array of route segments
 * @returns Total length in meters
 */
export function calculateRouteLength( segments: RouteSegment[] ): number {
    return segments.reduce( ( total, segment ) => total + segment.length, 0 );
}

/**
 * Get route bounds
 * @param segments - Array of route segments
 * @returns Bounds [[minLng, minLat], [maxLng, maxLat]]
 */
export function getRouteBounds( segments: RouteSegment[] ): [[number, number], [number, number]] | null {
    if ( segments.length === 0 ) {
        return null;
    }

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;

    segments.forEach( ( segment ) => {
        const [ startLng, startLat ] = segment.start;
        const [ endLng, endLat ] = segment.end;

        minLng = Math.min( minLng, startLng, endLng );
        minLat = Math.min( minLat, startLat, endLat );
        maxLng = Math.max( maxLng, startLng, endLng );
        maxLat = Math.max( maxLat, startLat, endLat );
    } );

    return [
        [ minLng, minLat ],
        [ maxLng, maxLat ],
    ];
}

/**
 * Convert an ItineraryDTO to route segments for navigation
 * Extracts all leg geometries and converts them to segments
 * @param itinerary - The itinerary to convert
 * @returns Array of route segments
 */
export function convertItineraryToSegments( itinerary: ItineraryDTO | null ): RouteSegment[] {
    if ( itinerary === null ) {
        return [];
    }

    const allSegments: RouteSegment[] = [];

    // Extract geometry from each leg
    for ( const leg of itinerary.legs ) {
        if ( leg.geometry?.path === undefined || leg.geometry.path === null ) {
            continue;
        }

        // Decode the polyline geometry
        const decodedPath = DecodePath( leg.geometry.path, leg.geometry.type, leg.geometry.precision );

        // Convert decoded path to coordinates array
        const coordinates: [number, number][] = decodedPath.map( ( point ) => [ point.lng, point.lat ] );

        // Convert coordinates to segments
        const legSegments = convertRouteToSegments( coordinates );

        allSegments.push( ...legSegments );
    }

    return allSegments;
}
